import { Link, Stack, Typography } from '@mui/material'
import * as Yup from 'yup'

import theme from '~/app/providers/ui/theme'
import amyCharacterSrc from '~/assets/images/characters/amy.webp'
import ashaCharacterSrc from '~/assets/images/characters/asha.webp'
import claraCharacterSrc from '~/assets/images/characters/clara.webp'
import danCharacterSrc from '~/assets/images/characters/dan.webp'
import jaxCharacterSrc from '~/assets/images/characters/jax.webp'
import louisCharacterSrc from '~/assets/images/characters/louis.webp'
import myraCharacterSrc from '~/assets/images/characters/myra.webp'
import oliviaCharacterSrc from '~/assets/images/characters/olivia.webp'
import richardCharacterSrc from '~/assets/images/characters/richard.webp'
import { THREE_DIGITS_REGEX } from '~/constants'
import { ETextStatus } from '~/features/settings/models'

export const isNumeric = (value: string) => /^\d*$/.test(value)

export const voiceOptions = [
  {
    id: 'ftsyff0MFCU1XIAcYCvJ',
    src: oliviaCharacterSrc,
    description: 'Jacqueline - casual and clear', // bw
  },
  {
    id: 'grkOBjhN4PebqeZFaEUR',
    src: myraCharacterSrc,
    description: 'Myra - upbeat and lively', // blw
  },
  {
    id: 'ChvsWYb6mMZv4CzDnRix',
    src: amyCharacterSrc,
    description: 'Amy - clear and concise', // w
  },
  {
    id: 'FISo3sWdWP0bALUdgh5x',
    src: danCharacterSrc,
    description: 'Dan - fast and crisp', // dm
  },
  {
    id: 'XXYRcwXlEHZ1DbdlGWh6',
    src: jaxCharacterSrc,
    description: 'Jax - mature and deep', // bm
  },
  {
    id: 'aGkVQvWUZi16EH8aZJvT',
    src: richardCharacterSrc,
    description: 'Richard - Australian accent', // semi humorous one.
  },
  {
    id: 'UOIqAnmS11Reiei1Ytkc',
    src: claraCharacterSrc,
    description: 'Carla - native Spanish speaker',
  },
  {
    id: 'gHu9GtaHOXcSqFTK06ux',
    src: ashaCharacterSrc,
    description: 'Asha - native Hindi speaker',
  },
  {
    id: '1a3lMdKLUcfcMtvN772u',
    src: louisCharacterSrc,
    description: 'Louis - native French speaker',
  },
  // Add more images as needed
]

export const areaCodeValidationSchema = Yup.object().shape({
  areaCode: Yup.string().matches(
    THREE_DIGITS_REGEX,
    'The area code must be a 3 digit number.',
  ),
})

export const libbyVoiceValidationSchema = Yup.object().shape({
  phoneIntro: Yup.string()
    .min(30, 'Your intro message must be between 30 and 250 characters long.')
    .max(250, 'Your intro message must be between 30 and 250 characters long.')
    .required('Intro message is required'),
  agentLanguage: Yup.string()
    .required('Please select a language')
    .min(1, 'Please select a language'),
  phoneVoice: Yup.string()
    .required('Please select a voice')
    .min(1, 'Please select a voice'),
})

export const getTextSettingsText = (
  textStatus: ETextStatus,
  phoneNumber: string,
  supportEmail: string,
) => {
  if (textStatus === ETextStatus.active) {
    return (
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
          }}
        >
          Texting Status: Active
        </Typography>
        <Typography variant="body2">
          Texting has been activated for your phone number! Text{` `}
          <Typography
            component="span"
            variant="body2"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
            }}
          >
            {phoneNumber}{' '}
          </Typography>
          to try it out, and share on your website, social media platforms,
          email signature and wherever potential customers may find you. Email
          us at <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link> if
          you would like to deactivate texting for your phone number.
        </Typography>
      </Stack>
    )
  }

  if (textStatus === ETextStatus.settingUp) {
    return (
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
          }}
        >
          Texting Status: Setting up texting for your account
        </Typography>
        <Typography variant="body2">
          We&apos;ve received your request to set up texting for your account.
          If you have questions about the process or your status within the
          process, then reach out to us at{' '}
          <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link>.
        </Typography>
      </Stack>
    )
  }

  return (
    <Typography variant="body2">
      Email us at <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link> to
      set up your number for texting. (There&apos;s an approval process we need
      to run with the phone carrier.)
    </Typography>
  )
}

export const forwardingLinks = [
  {
    url: 'https://www.att.com/device-support/article/wireless/KM1320467/ATT/WirelessNetMF279',
    name: 'AT&T',
  },
  {
    url: 'https://www.xfinity.com/support/articles/forward-calls-with-call-forwarding',
    name: 'Comcast or Xfinity',
  },
  {
    url: 'https://www.t-mobile.com/support/plans-features/calling-services#steps4',
    name: 'T-Mobile or Sprint',
  },
  {
    url: 'https://www.verizon.com/support/call-forwarding-faqs/#:~:text=To%20turn%20on%20Call%20Forwarding,908%2D123%2D4567).',
    name: 'Verizon',
  },
]
